import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { orgSetupFactsKey } from "@/api/queryKeys";

// ---- Per-org identity / contact info ------------------------------

export type OrgProfileView = {
  org_slug: string;
  address: string | null;
  contact_email: string | null;
  invoice_email: string | null;
  phone: string | null;
  registration_number: string | null;
  vat_number: string | null;
  website: string | null;
  updated_at: string | null;
  updated_by: string | null;
};

const orgProfileKey = (slug: string) =>
  ["orgs", slug, "profile"] as const;

export function useOrgProfile(slug: string) {
  return useQuery({
    queryKey: orgProfileKey(slug),
    queryFn: async () =>
      (await api.get<OrgProfileView>(`/orgs/${slug}/profile`)).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

/** Org-wide facts behind the setup checklist that any member may read.
 *  Booleans only: no other member's sessions or accounts are listed. */
export type OrgSetupFacts = {
  /** The org has at least one session (not deleted), whoever started it. */
  has_sessions: boolean;
  /** A current member of the org has a usable agent login (or the org
   *  is covered by a deployment-wide central-billing/system-key) for a
   *  runtime the server registers. Drives the credential step for a
   *  viewer who isn't a member of the org (e.g. a superadmin). */
  member_has_agent_login: boolean;
};

export function useOrgSetupFacts(slug: string) {
  return useQuery({
    queryKey: orgSetupFactsKey(slug),
    queryFn: async () =>
      (await api.get<OrgSetupFacts>(`/orgs/${slug}/setup-state`)).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export function useSetOrgProfile(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<OrgProfileView, "org_slug" | "updated_at" | "updated_by">) =>
      (await api.put<OrgProfileView>(`/orgs/${slug}/profile`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgProfileKey(slug) });
    },
  });
}
